import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Reveal from "@/components/base/Reveal";
import useSeo from "@/hooks/useSeo";
import { rankMonth, rankToday, rankWeek } from "@/mocks/rankings";
import { topics } from "@/mocks/topics";
import { formatHeat } from "@/utils/format";
import type { Topic } from "@/types/content";

type TabKey = "today" | "week" | "month";

const tabs: { key: TabKey; label: string; desc: string }[] = [
  { key: "today", label: "今日热榜", desc: "过去 24 小时内讨论增速最快的话题" },
  { key: "week", label: "本周热榜", desc: "最近七天内持续发酵、评论区最活跃的话题" },
  { key: "month", label: "本月热榜", desc: "近三十天累计讨论量最高的长线话题" },
];

type RankItem = { topicId: string; heat: number; change: number };

export default function RankPage() {
  const [tab, setTab] = useState<TabKey>("today");

  useSeo({
    title: "吃瓜热榜 - 深夜吃瓜 · 今日/本周/本月热话题排行",
    description:
      "深夜吃瓜热榜按浏览量、评论数、点赞密度与讨论增速综合计算，提供今日、本周、本月三个维度的热话题排行，一眼看到此刻正在刷屏的讨论。",
    keywords: "吃瓜热榜,热榜,深夜吃瓜,热门话题,排行,讨论热度",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "深夜吃瓜热榜",
        description: "今日、本周、本月三个维度的热话题排行。",
        inLanguage: "zh-CN",
        url: "https://banhuita.cn/rank",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://banhuita.cn/" },
          { "@type": "ListItem", position: 2, name: "吃瓜热榜", item: "https://banhuita.cn/rank" },
        ],
      },
    ],
  });

  const currentTab = tabs.find((t) => t.key === tab)!;

  const list = useMemo(() => {
    const source: RankItem[] = tab === "today" ? rankToday : tab === "week" ? rankWeek : rankMonth;
    return source
      .map((item) => ({ ...item, topic: topics.find((t) => t.id === item.topicId) as Topic | undefined }))
      .filter((item): item is RankItem & { topic: Topic } => Boolean(item.topic));
  }, [tab]);

  const maxHeat = list.reduce((max, item) => Math.max(max, item.heat), 1);

  return (
    <SiteLayout>
      <section className="border-b border-background-200/60 bg-background-100/60">
        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: "吃瓜热榜" }]} />
          <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="heading-serif text-3xl font-black text-foreground-950 md:text-4xl">
                吃瓜热榜 · 此刻最该看的讨论
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-500 md:text-base">
                热度由浏览量、评论数、点赞密度与讨论增速综合计算得出。越靠近当下发生的讨论，权重越高。
              </p>
            </div>
            <div className="flex items-center gap-1 self-start rounded-full bg-background-200/70 p-1 lg:self-auto">
              {tabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                    tab === item.key
                      ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <p className="mb-6 flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-information-line text-accent-500" />
            {currentTab.desc}
          </p>

          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <ol className="lg:col-span-8 space-y-3">
              {list.map((item, index) => {
                const isTop = index < 3;
                return (
                  <Reveal key={item.topicId} delay={Math.min(index, 8) * 40}>
                    <li>
                      <Link
                        to={`/topics/${item.topic.id}`}
                        className="group flex items-center gap-4 rounded-lg border border-background-200/80 bg-background-100 p-4 transition-all duration-300 hover:border-primary-500/50 hover:bg-background-100 md:p-5"
                      >
                        <span
                          className={`brand-font flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-lg ${
                            isTop
                              ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                              : "bg-background-200 text-foreground-700"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <h2 className="truncate text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-500 md:text-base">
                            {item.topic.title}
                          </h2>
                          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                            <span className="rounded-full bg-secondary-100/80 px-2 py-0.5 text-secondary-900">
                              {item.topic.categoryName}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-chat-3-line" />
                              {item.topic.comments}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line" />
                              {formatHeat(item.topic.views)}
                            </span>
                          </div>
                          <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500"
                              style={{ width: `${Math.max(12, Math.round((item.heat / maxHeat) * 100))}%` }}
                            />
                          </div>
                        </div>
                        <div className="hidden shrink-0 flex-col items-end sm:flex">
                          <span className="brand-font text-lg text-primary-500">
                            {formatHeat(item.heat)}
                          </span>
                          <span
                            className={`mt-1 flex items-center gap-0.5 text-xs ${
                              item.change >= 0 ? "text-primary-500" : "text-secondary-500"
                            }`}
                          >
                            <i className={item.change >= 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
                            {Math.abs(item.change)} 位
                          </span>
                        </div>
                      </Link>
                    </li>
                  </Reveal>
                );
              })}
            </ol>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 lg:space-y-6">
                <div className="rounded-lg border border-background-200/80 bg-background-100 p-5">
                  <h2 className="heading-serif flex items-center gap-2 text-base font-bold text-foreground-950">
                    <i className="ri-bar-chart-box-line text-primary-500" />
                    榜单说明
                  </h2>
                  <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
                    <li className="flex gap-2.5">
                      <i className="ri-checkbox-circle-line mt-0.5 text-secondary-500" />
                      热度每 15 分钟刷新一次，排序会随讨论实时变化。
                    </li>
                    <li className="flex gap-2.5">
                      <i className="ri-checkbox-circle-line mt-0.5 text-secondary-500" />
                      「今日」侧重增速，「本月」侧重累计讨论量。
                    </li>
                    <li className="flex gap-2.5">
                      <i className="ri-checkbox-circle-line mt-0.5 text-secondary-500" />
                      纯情绪刷屏不计入热度，有信息量的观点权重更高。
                    </li>
                  </ul>
                </div>

                <div className="rounded-lg border border-accent-500/30 bg-accent-50/40 p-5">
                  <h2 className="heading-serif text-base font-bold text-foreground-950">
                    想把话题送上榜？
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    去任意话题的讨论区发表有信息量的观点，被点赞和回复越多，这个话题的热度就越高。
                  </p>
                  <Link
                    to="/topics"
                    className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
                  >
                    <i className="ri-fire-line" />
                    去贡献热度
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}